import { useCallback, useEffect, useRef, useState } from 'react';

import { loadSongReference } from '../melody';

import type { LoadedSongReference } from '../melody';

/**
 * useSongReference — 노래 따라부르기의 레퍼런스(멜로디 + 음원)를 **녹음 전에** 받아 둔다.
 *
 * 진단 화면에 들어서자마자 받는다: 노래는 마지막 진단이라 앞의 두 진단 동안 받아 두면
 * 사용자가 대기를 안 본다. 카운트다운이 끝난 뒤 재생이 실패하면 그 테이크는 채점할 수 없고
 * (부를 것을 못 들려줬으니 사용자 잘못도 아니다), 사용자는 20초를 버린 뒤에야 그걸 안다.
 * 못 받으면 그 진단의 가이드에서 재시도/건너뛰기를 낸다 — 진단 전체를 막지 않는다.
 *
 * 음원은 blob으로 받아 objectURL로 준다: `<audio src>`에 URL을 그대로 걸면 "받아졌나"를
 * 재생이 시작될 때까지 알 수 없다.
 */

export type SongReferenceStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface SongReferenceState {
  status: SongReferenceStatus;
  reference: LoadedSongReference | null;
  retry: () => void;
}

export const useSongReference = (): SongReferenceState => {
  const [status, setStatus] = useState<SongReferenceStatus>('idle');
  const [reference, setReference] = useState<LoadedSongReference | null>(null);
  const [attempt, setAttempt] = useState(0);
  // 받아 둔 objectURL은 컴포넌트가 사라질 때 되돌린다 — 안 지우면 blob이 문서 수명 내내 산다.
  // 둘을 따로 든다: 가이드가 따로 있는 곡은 blob도 둘이다(가이드가 없으면 같은 URL이라
  // 중복 revoke가 되는데, 이미 되돌린 URL을 다시 되돌리는 것은 무해하다).
  const objectUrlRef = useRef<string[]>([]);

  useEffect(() => {
    if (reference != null) {
      return;
    }
    let cancelled = false;
    setStatus('loading');
    loadSongReference()
      .then((loaded) => {
        if (cancelled) {
          // 이미 떠난 화면의 blob — 여기서 안 지우면 참조 없는 URL이 남는다.
          URL.revokeObjectURL(loaded.audioObjectUrl);
          URL.revokeObjectURL(loaded.guideAudioObjectUrl);
          return;
        }
        objectUrlRef.current = [
          loaded.audioObjectUrl,
          loaded.guideAudioObjectUrl,
        ];
        setReference(loaded);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reference, attempt]);

  useEffect(
    () => () => {
      objectUrlRef.current.forEach((url) => URL.revokeObjectURL(url));
    },
    [],
  );

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { status, reference, retry };
};
